import { createContext, useContext, useEffect, useState } from 'react'
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile,
  deleteUser,
} from 'firebase/auth'
import { get, getDatabase, ref, update } from 'firebase/database'
import { getProfileDisplayName } from '../utils/userProfile'
import { getAuthErrorMessage, normalizeEmail } from '../utils/authErrors'
import { checkDeviceAvailability, claimDeviceForUser, normalizeDeviceId } from '../utils/deviceId'

const AuthContext = createContext(null)

function wrapAuthError(error) {
  const friendly = new Error(getAuthErrorMessage(error) || error?.message || 'Erro de autenticação.')
  friendly.code = error?.code
  return friendly
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const auth = getAuth()
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser)
      setLoading(false)
      if (firebaseUser) {
        upsertUserProfile(firebaseUser).catch((e) => console.warn('Falha ao atualizar perfil no DB:', e))
      }
    })
    return () => unsub()
  }, [])

  const login = async (email, password) => {
    const auth = getAuth()
    try {
      const cred = await signInWithEmailAndPassword(auth, normalizeEmail(email), password)
      await upsertUserProfile(cred.user)
    } catch (error) {
      throw wrapAuthError(error)
    }
  }

  const register = async ({ name, email, password, deviceId }) => {
    const auth = getAuth()
    const cleanDeviceId = normalizeDeviceId(deviceId)
    if (!cleanDeviceId) {
      throw new Error(
        'Informe o ID do dispositivo. É necessário possuir um Sys[mv] para criar a conta. Se comprou e ainda não chegou, aguarde o aparelho com o ID.'
      )
    }

    const availability = await checkDeviceAvailability(cleanDeviceId)
    if (!availability.available) {
      throw new Error(availability.message || 'Verifique se o ID informado está correto.')
    }

    let cred
    try {
      cred = await createUserWithEmailAndPassword(auth, normalizeEmail(email), password)
    } catch (error) {
      throw wrapAuthError(error)
    }

    try {
      if (name) {
        await updateProfile(cred.user, { displayName: name })
      }

      const db = getDatabase()
      await update(ref(db, `users/${cred.user.uid}/profile`), {
        name: name || cred.user.displayName || '',
        email: cred.user.email || '',
        phone: '',
        memberSince: new Date().toISOString(),
        notificationsEnabled: true,
        subscriptionPlan: 'gratuito',
        historyRetentionDays: 30,
        privacyPolicyAccepted: true,
        privacyPolicyAcceptedDate: new Date().toISOString(),
        emailEnabled: true,
      })

      await upsertUserProfile(cred.user)
      await claimDeviceForUser(cred.user, cleanDeviceId, { displayName: name })
    } catch (error) {
      try {
        await deleteUser(cred.user)
      } catch (rollbackErr) {
        console.warn('Falha ao reverter conta após erro no vínculo do dispositivo:', rollbackErr)
      }
      throw error?.code ? wrapAuthError(error) : error
    }
  }

  const logout = async () => {
    const auth = getAuth()
    await signOut(auth)
  }

  const loginWithGoogle = async ({ deviceId, intent = 'login' } = {}) => {
    const auth = getAuth()
    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({ prompt: 'select_account' })
    const cleanDeviceId = normalizeDeviceId(deviceId)

    try {
      const cred = await signInWithPopup(auth, provider)
      const user = cred.user
      const db = getDatabase()
      const profileSnap = await get(ref(db, `users/${user.uid}/profile`))
      const isNewUser = !profileSnap.exists()

      if (isNewUser) {
        if (intent !== 'register' || !cleanDeviceId) {
          try {
            await deleteUser(user)
          } catch (e) {
            console.warn('Falha ao remover conta Google sem dispositivo:', e)
          }
          throw new Error(
            'Para criar uma conta, use Cadastrar e informe o ID do seu dispositivo Sys[mv]. Se ainda não recebeu o aparelho, aguarde chegar com o ID.'
          )
        }

        const availability = await checkDeviceAvailability(cleanDeviceId)
        if (!availability.available) {
          try {
            await deleteUser(user)
          } catch (e) {
            console.warn('Falha ao remover conta Google com ID inválido:', e)
          }
          throw new Error(availability.message || 'Verifique se o ID informado está correto.')
        }

        try {
          await update(ref(db, `users/${user.uid}/profile`), {
            name: user.displayName || 'Usuário Google',
            email: user.email || '',
            phone: '',
            memberSince: user.metadata?.creationTime || new Date().toISOString(),
            notificationsEnabled: true,
            subscriptionPlan: 'gratuito',
            historyRetentionDays: 30,
            privacyPolicyAccepted: true,
            privacyPolicyAcceptedDate: new Date().toISOString(),
            emailEnabled: true,
          })
          await upsertUserProfile(user)
          await claimDeviceForUser(user, cleanDeviceId, { displayName: user.displayName })
        } catch (error) {
          try {
            await deleteUser(user)
          } catch (rollbackErr) {
            console.warn('Falha ao reverter conta Google após erro no vínculo:', rollbackErr)
          }
          throw error
        }
        return
      }

      await upsertUserProfile(user)
    } catch (error) {
      if (error?.code) throw wrapAuthError(error)
      throw error
    }
  }

  const resetPassword = async (email) => {
    const auth = getAuth()
    try {
      await sendPasswordResetEmail(auth, normalizeEmail(email))
    } catch (error) {
      throw wrapAuthError(error)
    }
  }

  const upsertUserProfile = async (firebaseUser) => {
    if (!firebaseUser?.uid) return

    const db = getDatabase()
    await update(ref(db, `users/${firebaseUser.uid}`), {
      email: firebaseUser.email || null,
      displayName: firebaseUser.displayName || null,
      photoURL: firebaseUser.photoURL || null,
      lastLoginAt: Date.now(),
    })
  }

  const updateMyProfile = async ({ displayName, phone }) => {
    const auth = getAuth()
    const current = auth.currentUser
    if (!current) throw new Error('Usuário não autenticado.')

    const name =
      typeof displayName === 'string' && displayName.trim()
        ? displayName.trim()
        : getProfileDisplayName(null, current.displayName)

    const phoneValue = typeof phone === 'string' ? phone.trim() : ''

    if (name && name !== current.displayName) {
      await updateProfile(current, { displayName: name })
      setUser(auth.currentUser)
    }

    const db = getDatabase()
    const uid = current.uid

    await update(ref(db, `users/${uid}`), {
      displayName: name || null,
      name: name || null,
      phone: phoneValue || null,
      updatedAt: Date.now(),
    })

    await update(ref(db, `users/${uid}/profile`), {
      name: name || null,
      phone: phoneValue || null,
      email: current.email || null,
    })
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle, resetPassword, updateMyProfile }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
